<template>
  <div class="storefinder">
    <form class="storefinder__form" @submit.prevent="onSubmit">

      <!-- Introduction and location -->
      <section class="contentWrapper offsetLeft-noOuterGap--1 offsetRight-noOuterGap--1 storefinder__searchSection" ref="searchSection">
        <div>
          <div class="storefinder__searchFormDescription" v-html="searchFormDescription"></div>
          <div class="storefinder__inputFields">
            <input :placeholder="staticTextZipOrCity" type="text" v-model="form.search" @input="updateInputCache"/>
            <select v-model="form.country" @change="updateInputCache">
              <option v-for="option in countryOptions" :value="option.value">{{ option.label }}</option>
            </select>
            <div class="storefinderCheckbox__group">
              <label class="storefinderAccordion__label" v-for="rootPage in pageTree">
                <input type="checkbox" :value="rootPage.page.uid" class="storefinderAccordion__checkbox" v-model="selectedRootPages" @change="onRootPageSelect">
                <span class="storefinderAccordion__category">{{ rootPage.page.title }}</span>
              </label>
            </div>
          </div>
        </div>

        <div>
          <div class="storefinder__bookmarkDescription" v-html="bookmarkDescription"></div>
          <button class="storefinder__button" type="button" @click="onLoadBookmarks" :disabled="!hasBookmarks">♥ {{ staticTextLoadBookmarks }} ({{bookmarks.length}})</button>
        </div>
      </section>

      <!-- Accordion and filter -->
      <section v-show="showAccordionSection" class="contentWrapper offsetLeft-noOuterGap--1 offsetRight-noOuterGap--1 storefinder__accordionSection" ref="accordionSection">
        <div class="storefinder__accordionTitle">{{ staticTextYourSearchOptions }}</div>
        <StorefinderAccordion :pageTree="pageTree" :submitButtonText="submitButtonText" ref="accordion"/>
      </section>



      <section class="contentWrapper offsetLeft-noOuterGap--1 offsetRight-noOuterGap--1 storefinder__searchSection" ref="searchSection">
        <button class="storefinder__button" :disabled="!canSubmit">{{ submitButtonText }}</button>
        <p class="storefinder__error" v-for="error in errors">{{ error.message }}</p>
      </section>

      <!-- Map -->
      <div ref="mapSection"></div>
      <storefinder-map class="contentWrapper contentWrapper--fullWidth" :api-key="mapsApiKey" :config="mapConfig" :zoom="zoom" :position="position">
        <div class="opt-in__container" slot="optIn" slot-scope="optIn">
          <div class="text--center" v-html="googleMapsOptinText"></div>
          <button class="storefinder__button" @click="optIn.allowMap" v-html="googleMapsOptinLabel"></button>
        </div>
        <div slot-scope="map">
          <storefinder-map-marker v-for="store in stores" :cluster-size="stores.length" :store="store" :key="store.uid" v-if="map.isOptedIn" :image="{src: '/typo3conf/ext/storefinder/Resources/Public/Icons/K.svg', width: 32, height: 38}">
            <div slot="infoWindow" class="infowindow">
              <h4 class="infoWindow__company">
                {{ store.company }} <br>
                <small class="distance" v-if="store.distance">{{ Math.round(store.distance * 10) / 10 + ' km' }}</small>
              </h4>
              <p class="infoWindow__address">
                {{ store.address }} <br>
                {{ store.zip }} {{ store.city }} <br>
                <template v-if="store.www">
                  {{ store.www }} <br>
                </template>
              </p>
              <ul class="infoWindow__categoryList">
                <li class="infoWindow__categoryListItem" v-for="tag in store.tags" :key="tag.uid" v-if="tag.image">
                  <img class="infoWindow__categoryIcon" :src="tag.image" :alt="tag.tagTitle" :title="tag.tagTitle">
                </li>
              </ul>
              <p>
                <a class="button storefinder__button storefinder__button--infoWindow" :href="'https://maps.google.com/?daddr='+ store._string" target="_blank">
                  {{staticTextDirections}}
                </a>
              </p>
            </div>
          </storefinder-map-marker>
        </div>

      </storefinder-map>

      <!-- Result list -->
      <section class="contentWrapper storefinder__resultSection" ref="resultSection">
        <header class="offsetLeft-noOuterGap--1 offsetRight-noOuterGap--1 storefinder__resultHeader">
          <div class="headline--3">{{ statusText }}</div>
        </header>
        <div v-if="isLoading">
          <div class="storefinder__resultsTotal">
            <LoadingSpinner/>
          </div>
        </div>
        <div v-else>
          <!-- Stores -->
          <article v-if="stores.length" class="offsetLeft-noOuterGap--1 offsetRight-noOuterGap--1 storefinder__resultListWrapper">
            <header class="headline--3 storefinder__resultListHeader">{{ defaultResultListTitle }}</header>
            <div class="storefinder__resultList">
              <StorefinderResult
                class="storefinder__resultList__result"
                v-for="(store, key) in stores.slice(0, resultLimit)"
                :store="store"
                :key="key"
                @contact="onContact"
                @select="scrollToMap"
                :static-text-phone="staticTextPhone"
                :static-text-web="staticTextWeb"
                :static-text-email="staticTextEmail"
                :static-text-directions="staticTextDirections"
                :static-text-contact-button="staticTextContactButton"
              />
            </div>
          </article>

          <!-- Load more button -->
          <div v-if="canShowMore" class="storefinder__loadMoreWrapper">
            <button class="storefinder__button storefinder__loadMoreButton" type="button" @click="onShowMore">{{ staticTextShowMore }}</button>
          </div>
        </div>
      </section>
    </form>

    <StorefinderContactDialog
      ref="contactDialog"
      :contact="selectedContact"
      :contactModalTitle="contactModalTitle"
      :contactMessageLabel="contactMessageLabel"
      :contactMessagePrefix="contactMessagePrefix"
      :contactMessageSuffix="contactMessageSuffix"
      :staticTextFormSalutation="staticTextFormSalutation"
      :staticTextFormSalutationMister="staticTextFormSalutationMister"
      :staticTextFormSalutationMisses="staticTextFormSalutationMisses"
      :staticTextFormName="staticTextFormName"
      :staticTextFormStreetNumber="staticTextFormStreetNumber"
      :staticTextFormZipCode="staticTextFormZipCode"
      :staticTextFormPhone="staticTextFormPhone"
      :staticTextFormEmail="staticTextFormEmail"
      :staticTextFormDataprotection="staticTextFormDataprotection"
      :staticTextFormSubmit="staticTextFormSubmit"
      :staticTextFormSuccess="staticTextFormSuccess"
      :staticTextFormError="staticTextFormError"
    >
      <template v-slot:modal-close-text>{{ staticTextCloseWindow }}</template>
    </StorefinderContactDialog>
  </div>
</template>

<script>
  import axios from 'axios';
  import VueScrollTo from 'vue-scrollto';
  import MapTheme from '../../Scripts/MapTheme';
  import countries from '../CountryJson';
  import {mapGetters} from 'vuex'

  export default {
    name: 'storefinder',

    props: [
      'pageTree',
      'mapsApiKey',
      'countryOptions',
      'radiusOptions',
      'defaultCountry',
      'defaultRadius',
      'searchFormDescription',
      'bookmarkDescription',
      'resultSectionTitle',
      'premiumResultListTitle',
      'defaultResultListTitle',
      'contactModalTitle',
      'contactMessageLabel',
      'contactMessagePrefix',
      'contactMessageSuffix',
      'staticTextShowMore',
      'staticTextLoading',
      'staticTextStoresFound',
      'staticTextFindStores',
      'staticTextZipOrCity',
      'staticTextCountry',
      'staticTextRadius',
      'staticTextLoadBookmarks',
      'staticTextYourSearchOptions',
      'staticTextErrorInput',
      'staticTextErrorRadius',
      'staticTextErrorAddress',
      'staticTextErrorUnknown',
      'staticTextPhone',
      'staticTextWeb',
      'staticTextEmail',
      'staticTextDirections',
      'staticTextContactButton',
      'staticTextCloseWindow',
      'staticTextFormSalutation',
      'staticTextFormSalutationMister',
      'staticTextFormSalutationMisses',
      'staticTextFormName',
      'staticTextFormStreetNumber',
      'staticTextFormZipCode',
      'staticTextFormPhone',
      'staticTextFormEmail',
      'staticTextFormDataprotection',
      'staticTextFormSubmit',
      'staticTextFormSuccess',
      'staticTextFormError',
      'googleMapsOptinText',
      'googleMapsOptinLabel',
    ],

    data() {
      return {
        errors: [],
        isLoading: false,
        resultLimit: 10,
        selectedContact: null,
        pages:{},
        position: {},
        coordinates: null,
        form: {
          search: localStorage.getItem('search') || '',
          country: localStorage.getItem('country') || this.defaultCountry,
          radius: localStorage.getItem('radius') || this.defaultRadius,
        },
        visibility:{}
      };
    },

    methods: {
      readPages(){
        const pages = {};
        const addPageRecursive = ({ children, page })=>{
          children = Object.values(children);
          pages[page.uid] = page;
          // console.log(pageTree.children.length)
          if(children.length > 0){
            children.forEach(addPageRecursive);
          }
        }
        this.pageTree.forEach(addPageRecursive);
        this.pages = pages;
      },
      onRootPageSelect() {
        this.updateInputCache();
        this.updateTopFilter();
      },
      scrollToMap() {
        VueScrollTo.scrollTo(this.$refs.mapSection, 500, { offset: -120 });
      },
      onShowMore() {
        this.resultLimit += 10;
      },
      onContact(store) {
        this.selectedContact = store;
        this.$refs.contactDialog.toggleModal();
      },
      onSubmit() {
        if (!this.canSubmit) {
          this.errors.push({
            message: this.staticTextErrorInput,
          });

          VueScrollTo.scrollTo(this.$refs.searchSection, 500, { offset: -120 });
        } else {
          this.$store.dispatch('updateHash');
          this.fetchResults();
        }
      },
      updateTopFilter(){
        this.pageTree.forEach((pageComponent)=>{
          const title = pageComponent.page.title;
          const id = pageComponent.page.uid
          this.visibility['show_'+title.toLowerCase()] = this.selectedRootPages.includes(id);
        });
      },
      updateInputCache() {
        localStorage.setItem('search', this.form.search);
        localStorage.setItem('radius', this.form.radius);
        localStorage.setItem('country', this.form.country);
        localStorage.setItem('selectedRootPages', this.$store.getters.selectedRootPages.join(','));
      },
      loadInputCache() {
        const selectedRootPages = localStorage.getItem('selectedRootPages');
        if (selectedRootPages) {
          this.$store.commit('updateSelectedRootPages', selectedRootPages.split(',').map(uid => parseInt(uid)));
        }
        this.updateTopFilter();
      },
      fetchResults() {
        if (!this.canSubmit) {
          this.$store.commit("setStores", []);

          return;
        }

        this.errors = [];
        this.isLoading = true;

        const urlParts = [
          `search=${this.form.search}`,
          `country=${this.form.country}`,
          `radius=${this.form.radius}`,
          `tags=${this.filterTags.map(tag => tag.uid).join(',')}`,
        ];
        for(const url in this.visibility){
          urlParts.push(url+"="+this.visibility[url]);
        }

        axios.get(`/?storefinderAjaxAction=find&${urlParts.join('&')}`)
          .then(response => {
            const stores = response.data.data || [];

            if (response.data.meta.error) {
              this.errors.push({ message: response.data.meta.error });
            } else if (response.data.meta.location.status === 'OK') {
              // Update map.
              const lat = response.data.meta.location.results[0].geometry.location.lat;
              const lng = response.data.meta.location.results[0].geometry.location.lng;
              this.coordinates = { lat, lng };
              this.$store.commit("setStores", stores.map((store)=>{
                // adding a property to toggle to open the info window reactivly in the map marker.
                store.selected = false;
                return store;
              }));

              // Decide where to scroll to.
              if (this.stores.length > 0) {
                // Scroll to map section.
                VueScrollTo.scrollTo(this.$refs.mapSection, 500, { offset: -120  });
              } else {
                // Google found something, but we have no matching stores in our database.
                this.errors.push({
                  message: this.staticTextErrorRadius,
                });

                // Scoll to search section.
                VueScrollTo.scrollTo(this.$refs.searchSection, 500, { offset: -120 });
              }
            } else if (response.data.meta.location.status === 'ZERO_RESULTS') {
              // Google could not locate the given address.
              this.errors.push({ message: this.staticTextErrorInput });

              // Scroll to search section.
              VueScrollTo.scrollTo(this.$refs.searchSection, 500, { offset: -120 });
            } else if (response.data.meta.location.status === 'REQUEST_DENIED') {
              // There is an issue with the given API key.
              this.errors.push({ message: response.data.meta.location['error_message'] });

              // Scroll to search section.
              VueScrollTo.scrollTo(this.$refs.searchSection, 500, { offset: -120 });
            }
          })
          .catch(error => {
            console.log('error', error);
            this.errors.push({ message: this.staticTextErrorUnknown });

            VueScrollTo.scrollTo(this.$refs.searchSection, 500, { offset: -120 });
          })
          .then(() => {
            this.isLoading = false;
          });
      },
      onLoadBookmarks() {
        this.$refs.accordion.loadBookmarks();
        this.selectParentCategories();
        this.$refs.accordion.openPanelsWithSelections();
        VueScrollTo.scrollTo(this.$refs.accordionSection, 500, { offset: -120 });
      },
      byDistance(a, b) {
        return a.distance - b.distance;
      },
      selectParentCategories() {
        this.$store.getters.bookmarks.forEach(bookmark => {
          // Find the parent of this bookmark in the page tree.
          this.pageTree.forEach(rootPage => {
            Object.values(rootPage.children).forEach(parent => {
              const hasBookmark = Object.values(parent.children).some(child => child.page.uid === bookmark);
              if (hasBookmark && parent.page.storefinder_tag) {
                this.$store.commit('selectTag', parent.page.storefinder_tag.uid);
              }
            });
          });
        });
      },
      initGoogleMaps(){
        if(this.optInMap){
          // Setup the map module so it can be initialized on demand.
          // this.$store.dispatch('initializeMap');
        }
      }
    },
    computed: {
      ...mapGetters(["bookmarks", "stores"]),
      filterTags() {
        let filterTags = [];

        // Only request tags whose root page is selected ("Innen" or "Außen")
        this.pageTree.forEach(rootPage => {
          const isSelected = this.$store.getters.selectedRootPages.includes(rootPage.page.uid);

          if (isSelected) {
            Object.values(rootPage.children).forEach(parent => {
              const tagUid = parent.page.storefinder_tag ? parent.page.storefinder_tag.uid : 0;

              if (
                this.$store.getters.selectedTags.includes(tagUid)
                && !filterTags.some(tag => tag.uid === tagUid)
              ) {
                filterTags.push(parent.page.storefinder_tag);
              }
            });
          }
        });

        return filterTags;
      },
      selectedTags: {
        get() {
          return this.$store.getters.selectedTags;
        },
        set(value) {
          this.$store.commit('updateSelectedTags', value);
        },
      },
      selectedRootPages: {
        get() {
          return this.$store.getters.selectedRootPages;
        },
        set(value) {
          this.$store.commit('updateSelectedRootPages', value);
        },
      },
      showAccordionSection() {
        return this.pageTree.some((rootPage) => {
          return this.$store.getters.selectedRootPages.includes(rootPage.page.uid);
        });
      },
      hasBookmarks() {
        return Object.keys(this.pages).some((uid)=>{
          return this.bookmarks.includes(parseInt(uid));
        });
      },
      canShowMore() {
        // Pagination is disabled at customer request.
        return false;
      },
      maxRadius(){
        return this.stores.reduce((acc, store)=>{
          return acc >= store.distance ? acc : store.distance;
        }, 0)
      },
      canSubmit() {
        return (this.form.search && this.form.country);
      },
      zoom: function () {
        const radius = this.maxRadius;

        if (radius <= 25) {
          return 11;
        }

        if (radius <= 100) {
          return 9;
        }

        if (radius <= 200) {
          return 8;
        }

        return 6;
      },
      countryCenter() {
        const countryCenterPoints = countries[this.form.country];
        // Germany is default
        const mapCenter = { lat: 51.165691, lng: 10.451526 }

        if(countryCenterPoints){
          mapCenter.lat = parseFloat(countryCenterPoints.latitude);
          mapCenter.lng = parseFloat(countryCenterPoints.longitude);
        }
        return mapCenter
      },
      mapConfig() {
        return {
          zoom: this.zoom,
          center: this.countryCenter,
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          streetViewControl: true,
          rotateControl: true,
          fullscreenControl: true,
          styles: MapTheme,
        };
      },
      statusText() {
        return this.isLoading ? this.staticTextLoading : '';
      },
      submitButtonText() {
        return this.isLoading ? this.staticTextLoading : this.staticTextFindStores;
      },
    },

    mounted() {
      this.position = this.countryCenter;
      this.readPages();
      this.loadInputCache();
      this.$store.dispatch('selectTagsFromHash');
      if (this.$store.getters.preselectPage) {
        this.$refs.accordion.preselectPage();
      }
      this.$refs.accordion.openPanelsWithSelections();

      if (this.canSubmit) {
        this.fetchResults();
      }
    },
    watch:{
      coordinates: {
        deep: true,
        handler(val){
          this.position = val ? val : this.countryCenter;
        }
      },
    }
  };
</script>
